import type { TranslationMap } from "../lib/types.ts";

export const en: TranslationMap = {
  common: {
    version: "Version",
    health: "Health",
    ok: "OK",
    offline: "Offline",
    connect: "Connect",
    refresh: "Refresh",
    loading: "Loading…",
    refreshing: "Refreshing…",
    enabled: "Enabled",
    disabled: "Disabled",
    na: "n/a",
    docs: "Docs",
    resources: "Resources",
  },
  nav: {
    chat: "Chat",
    control: "Control",
    agent: "Agent",
    settings: "Settings",
    expand: "Expand sidebar",
    collapse: "Collapse sidebar",
  },
  tabs: {
    agents: "Agents",
    overview: "Overview",
    channels: "Channels",
    instances: "Instances",
    sessions: "Sessions",
    usage: "Usage",
    cron: "Cron Jobs",
    skills: "Skills",
    nodes: "Nodes",
    chat: "Chat",
    config: "Config",
    debug: "Debug",
    logs: "Logs",
  },
  subtitles: {
    agents: "Manage agent workspaces, tools, and identities.",
    overview: "Gateway status, entry points, and a fast health read.",
    channels: "Manage channels and settings.",
    instances: "Presence beacons from connected clients and nodes.",
    sessions: "Inspect active sessions and adjust per-session defaults.",
    usage: "Monitor API usage and costs.",
    cron: "Schedule wakeups and recurring agent runs.",
    skills: "Manage skill availability and API key injection.",
    nodes: "Paired devices, capabilities, and command exposure.",
    chat: "Direct gateway chat session for quick interventions.",
    config: "Edit ~/.openclaw/openclaw.json safely.",
    debug: "Gateway snapshots, events, and manual RPC calls.",
    logs: "Live tail of the gateway file logs.",
  },
  overview: {
    access: {
      title: "Gateway Access",
      subtitle: "Where the dashboard connects and how it authenticates.",
      wsUrl: "WebSocket URL",
      token: "Gateway Token",
      password: "Password (not stored)",
      sessionKey: "Default Session Key",
      language: "Language",
      connectHint: "Click Connect to apply connection changes.",
      trustedProxy: "Authenticated via trusted proxy.",
    },
    snapshot: {
      title: "Snapshot",
      subtitle: "Latest gateway handshake information.",
      status: "Status",
      uptime: "Uptime",
      tickInterval: "Tick Interval",
      lastChannelsRefresh: "Last Channels Refresh",
      channelsHint: "Use Channels to link WhatsApp, Telegram, Discord, Signal, or iMessage.",
    },
    stats: {
      instances: "Instances",
      instancesHint: "Presence beacons in the last 5 minutes.",
      sessions: "Sessions",
      sessionsHint: "Recent session keys tracked by the gateway.",
      cron: "Cron",
      cronNext: "Next wake {time}",
    },
    notes: {
      title: "Notes",
      subtitle: "Quick reminders for remote control setups.",
      tailscaleTitle: "Tailscale serve",
      tailscaleText: "Prefer serve mode to keep the gateway on loopback with tailnet auth.",
      sessionTitle: "Session hygiene",
      sessionText: "Use /new or sessions.patch to reset context.",
      cronTitle: "Cron reminders",
      cronText: "Use isolated sessions for recurring runs.",
    },
    auth: {
      required: "This gateway requires auth. Add a token or password, then click Connect.",
      failed:
        "Auth failed. Re-copy a tokenized URL with {command}, or update the token, then click Connect.",
    },
    pairing: {
      hint: "This device needs pairing approval from the gateway host.",
      mobileHint:
        "On mobile? Copy the full URL (including #token=...) from openclaw dashboard --no-open on your desktop.",
    },
    insecure: {
      hint: "This page is HTTP, so the browser blocks device identity. Use HTTPS (Tailscale Serve) or open {url} on the gateway host.",
      stayHttp: "If you must stay on HTTP, set {config} (token-only).",
    },
  },
  chat: {
    disconnected: "Disconnected from gateway.",
    refreshTitle: "Refresh chat data",
    thinkingToggle: "Toggle assistant thinking/working output",
    focusToggle: "Toggle focus mode (hide sidebar + page header)",
    onboardingDisabled: "Disabled during onboarding",
  },
  languages: {
    en: "English",
    zhCN: "简体中文 (Simplified Chinese)",
    zhTW: "繁體中文 (Traditional Chinese)",
    ptBR: "Português (Brazilian Portuguese)",
  },
  config: {
    settingsTitle: "Settings",
    allSettings: "All Settings",
    searchPlaceholder: "Search settings…",
    form: "Form",
    raw: "Raw",
    unsavedChanges: "Unsaved changes",
    unsavedChangesCount: "{count} unsaved changes",
    unsavedChangesCountOne: "1 unsaved change",
    noChanges: "No changes",
    reload: "Reload",
    save: "Save",
    apply: "Apply",
    update: "Update",
    loading: "Loading…",
    saving: "Saving…",
    applying: "Applying…",
    updating: "Updating…",
    viewPendingChanges: "View {count} pending changes",
    viewPendingChangesOne: "View 1 pending change",
    subnavAll: "All",
    valid: "valid",
    invalid: "invalid",
    unknown: "unknown",
    tagFiltersLabel: "Tag filters:",
    addTags: "Add tags",
    schemaUnavailable: "Schema unavailable.",
    unsupportedSchema: "Unsupported schema. Use Raw.",
    noMatch: "No settings match \"{query}\"",
    noSettingsInSection: "No settings in this section",
    formViewWarning: "Form view can't safely edit some fields. Use Raw to avoid losing config entries.",
    noItemsAdd: "No items yet. Click \"Add\" to create one.",
    add: "Add",
    removeItem: "Remove item",
    addEntry: "Add Entry",
    removeEntry: "Remove entry",
    noCustomEntries: "No custom entries.",
    keyPlaceholder: "Key",
    customEntriesLabel: "Custom entries",
    subsections: {
      meta: "Metadata",
      metaDesc: "Gateway metadata and version information",
      logging: "Logging",
      loggingDesc: "Log levels and output configuration",
      browser: "Browser",
      browserDesc: "Browser automation settings",
      ui: "UI",
      uiDesc: "User interface preferences",
      models: "Models",
      modelsDesc: "AI model configurations and providers",
      bindings: "Bindings",
      bindingsDesc: "Key bindings and shortcuts",
      broadcast: "Broadcast",
      broadcastDesc: "Broadcast and notification settings",
      audio: "Audio",
      audioDesc: "Audio input/output settings",
      session: "Session",
      sessionDesc: "Session management and persistence",
      cron: "Cron",
      cronDesc: "Scheduled tasks and automation",
      web: "Web",
      webDesc: "Web server and API settings",
      discovery: "Discovery",
      discoveryDesc: "Service discovery and networking",
      canvasHost: "Canvas Host",
      canvasHostDesc: "Canvas rendering and display",
      talk: "Talk",
      talkDesc: "Voice and speech settings",
      plugins: "Plugins",
      pluginsDesc: "Plugin management and extensions",
      approvals: "Approvals",
      approvalsDesc: "Approval and allowlist settings",
      memory: "Memory",
      memoryDesc: "Memory and context settings",
      media: "Media",
      mediaDesc: "Media storage and filename handling",
      nodeHost: "Node Host",
      nodeHostDesc: "Node host and browser proxy settings",
      diagnostics: "Diagnostics",
      diagnosticsDesc: "Diagnostics and troubleshooting",
    },
    sections: {
      env: "Environment",
      update: "Updates",
      agents: "Agents",
      auth: "Authentication",
      channels: "Channels",
      messages: "Messages",
      commands: "Commands",
      hooks: "Hooks",
      skills: "Skills",
      tools: "Tools",
      gateway: "Gateway",
      wizard: "Setup Wizard",
    },
    fields: {
      plugins: {
        allowlist: "Plugin Allowlist",
        allowlistDesc: "List of plugin packages allowed to load",
        denylist: "Plugin Denylist",
        denylistDesc: "List of plugin packages denied from loading",
        enablePlugins: "Enable Plugins",
        enablePluginsDesc:
          "Enable or disable plugin/extension loading globally during startup and config reload (default: true). Keep enabled only when extension capabilities are required by your deployment.",
        entries: "Plugin Entries",
        entriesDesc: "Installed or enabled plugin configuration",
        installRecords: "Plugin Install Records",
        installRecordsDesc: "Records and version info of installed plugins",
        loader: "Plugin Loader",
        loaderDesc: "Plugin loading and resolution configuration",
        slots: "Plugin Slots",
        slotsDesc: "Plugin slots and extension point configuration",
      },
    },
  },
  sessions: {
    fallback: {
      mainSession: "Main Session",
      subagent: "Subagent:",
      cronPrefix: "Cron:",
      cronJob: "Cron Job:",
    },
    channel: {
      bluebubbles: "iMessage",
      telegram: "Telegram",
      discord: "Discord",
      signal: "Signal",
      slack: "Slack",
      whatsapp: "WhatsApp",
      matrix: "Matrix",
      email: "Email",
      sms: "SMS",
    },
    groupSuffix: " Group",
    sessionSuffix: " Session",
  },
  theme: {
    label: "Theme",
    system: "System",
    light: "Light",
    dark: "Dark",
    systemAria: "System theme",
    lightAria: "Light theme",
    darkAria: "Dark theme",
  },
  commonDocsOpen: "Docs (opens in new tab)",
  agents: {
    cardTitle: "Agents",
    configured: "{count} configured.",
    configuredOne: "1 configured.",
    refresh: "Refresh",
    loading: "Loading…",
    noAgents: "No agents found.",
    selectAgent: "Select an agent",
    selectAgentSub: "Pick an agent to inspect its workspace and tools.",
    workspaceRouting: "Agent workspace and routing.",
    tabOverview: "Overview",
    tabFiles: "Files",
    tabTools: "Tools",
    tabSkills: "Skills",
    tabChannels: "Channels",
    tabCron: "Cron Jobs",
    overviewTitle: "Overview",
    overviewSub: "Workspace paths and identity metadata.",
    workspace: "Workspace",
    primaryModel: "Primary Model",
    identityName: "Identity Name",
    default: "Default",
    identityEmoji: "Identity Emoji",
    skillsFilter: "Skills Filter",
    allSkills: "all skills",
    selectedCount: "{count} selected",
    modelSelection: "Model Selection",
    primaryModelLabel: "Primary model (default)",
    primaryModelLabelShort: "Primary model",
    inheritDefault: "Inherit default",
    inheritDefaultWith: "Inherit default ({model})",
    fallbacks: "Fallbacks (comma-separated)",
    fallbacksPlaceholder: "provider/model, provider/model",
    reloadConfig: "Reload Config",
    save: "Save",
    yes: "yes",
    no: "no",
    unavailable: "Unavailable",
  },
  usage: {
    tokenUsage: "Token Usage",
    loading: "Loading",
    to: "to",
    subtitle: "See where tokens go, when sessions spike, and what drives cost.",
    filters: "Filters",
    filtersSelect: "Select a date range and click Refresh to load usage.",
    today: "Today",
    days7: "7d",
    days30: "30d",
    refresh: "Refresh",
    tokens: "Tokens",
    cost: "Cost",
    filterSessionsPlaceholder: "Filter sessions (e.g. key:agentmain:cron* model:gpt-4o has:errors minTokens:2000)",
    filterClientSide: "Filter (client-side)",
    sessionsInRange: "{count} sessions in range",
    sessionsInRangeZero: "0 sessions in range",
    sessionsMatch: "{shown} of {total} sessions match",
    tipFilters: "Tip: use filters or click bars to filter days.",
    activityByTime: "Activity by Time",
    activityByTimeDesc: "Estimates require session timestamps.",
    noTimelineData: "No timeline data yet.",
    noTimelineDataShort: "No timeline data",
    dailyUsage: "Daily Usage",
    noData: "No data",
    noDataInRange: "No data in range",
    sessions: "Sessions",
    avg: "avg",
    errors: "errors",
    all: "All",
    recentlyViewed: "Recently viewed",
    sortRecent: "Sort Recent",
    noSessionsInRange: "No sessions in range",
    shown: "{count} shown",
    shownZero: "0 shown",
    pin: "Pin",
    export: "Export",
    local: "Local",
    inRangeSub: "of {total} in range",
    noRecentSessions: "No recent sessions",
    clearSelection: "Clear Selection",
    sort: "Sort",
    sortCost: "Cost",
    sortErrors: "Errors",
    sortMessages: "Messages",
    sortOptionRecent: "Recent",
    sortTokens: "Tokens",
    sessionsCsv: "Sessions CSV",
    dailyCsv: "Daily CSV",
    overviewTitle: "Usage Overview",
    messagesCard: "Messages",
    toolCallsCard: "Tool Calls",
    errorsCard: "Errors",
    avgTokensPerMsg: "Avg Tokens / Msg",
    avgCostPerMsg: "Avg Cost / Msg",
    throughput: "Throughput",
    errorRate: "Error Rate",
    cacheHitRate: "Cache Hit Rate",
    hintMessages: "Total user + assistant messages in range.",
    hintToolCalls: "Total tool call count across sessions.",
    hintErrors: "Total message/tool errors in range.",
    hintTokensPerMsg: "Average tokens per message in this range.",
    hintCostPerMsg: "Average cost per message when providers report costs.",
    hintCostPerMsgMissing:
      "Average cost per message when providers report costs. Cost data is missing for some or all sessions in this range.",
    hintSessions: "Distinct sessions in the range.",
    hintThroughput: "Throughput shows tokens per minute over active time. Higher is better.",
    hintErrorRate: "Error rate = errors / total messages. Lower is better.",
    hintCacheHit: "Cache hit rate = cache read / (input + cache read). Higher is better.",
    userLabel: "user",
    assistantLabel: "assistant",
    toolsUsed: "tools used",
    toolResults: "tool results",
    callsShort: "calls",
    acrossMessages: "Across {count} messages",
    totalLabel: "total",
    avgSession: "avg session",
    cachedLabel: "cached",
    promptLabel: "prompt",
    topModels: "Top Models",
    topProviders: "Top Providers",
    topTools: "Top Tools",
    topAgents: "Top Agents",
    topChannels: "Top Channels",
    peakErrorDays: "Peak Error Days",
    peakErrorHours: "Peak Error Hours",
    noModelData: "No model data",
    noProviderData: "No provider data",
    noToolCalls: "No tool calls",
    noAgentData: "No agent data",
    noChannelData: "No channel data",
    noErrorData: "No error data",
    filterDays: "Days",
    filterHours: "Hours",
    daysUnit: "days",
    hoursUnit: "hours",
    filterSession: "Session",
    removeFilter: "Remove filter",
    clearAll: "Clear All",
    totalToggle: "Total",
    byType: "By Type",
    dailyTokenUsage: "Daily Token Usage",
    dailyCostUsage: "Daily Cost Usage",
    tokensByType: "Tokens by Type",
    costByType: "Cost by Type",
    output: "Output",
    input: "Input",
    cacheWrite: "Cache Write",
    cacheRead: "Cache Read",
    totalColon: "Total",
    copySessionName: "Copy session name",
    copy: "Copy",
    descending: "Descending",
    ascending: "Ascending",
    selectedCount: "Selected ({count})",
    moreSessions: "+{n} more",
    errorsDot: "errors",
    msgsShort: "msgs",
  },
  channels: {
    healthTitle: "Channel health",
    healthSub: "Channel status snapshots from the gateway.",
    noSnapshot: "No snapshot yet.",
  },
  instances: {
    cardTitle: "Connected Instances",
    cardSub: "Presence beacons from the gateway and clients.",
    noInstances: "No instances reported yet.",
    lastInput: "Last input {ago}",
    unknownHost: "unknown host",
    scopesCount: "{count} scopes",
    scopesLabel: "scopes: {list}",
    reason: "Reason",
  },
  sessionsPage: {
    cardTitle: "Sessions",
    cardSub: "Active session keys and per-session overrides.",
    activeWithin: "Active within (minutes)",
    limit: "Limit",
    includeGlobal: "Include global",
    includeUnknown: "Include unknown",
    store: "Store",
    key: "Key",
    label: "Label",
    kind: "Kind",
    updated: "Updated",
    tokens: "Tokens",
    thinking: "Thinking",
    verbose: "Verbose",
    reasoning: "Reasoning",
    actions: "Actions",
    noSessions: "No sessions found.",
    optionalPlaceholder: "(optional)",
    inherit: "inherit",
    delete: "Delete",
    verboseOff: "off (explicit)",
    verboseOn: "on",
    verboseFull: "full",
  },
  cron: {
    jobsTitle: "Jobs",
    jobsSub: "All scheduled jobs stored in the gateway.",
    runHistoryTitle: "Run history",
    runHistorySub: "Recent runs and delivery status.",
    noMatchingJobs: "No matching jobs.",
    noMatchingRuns: "No matching runs.",
    loadMoreJobs: "Load more jobs",
    loadMoreRuns: "Load more runs",
    editJob: "Edit Job",
    newJob: "New Job",
    newJobSub: "Schedule a wakeup or recurring run.",
    editJobSub: "Update schedule, payload, or delivery.",
    saving: "Saving…",
    addJob: "Add job",
    saveChanges: "Save changes",
    allStatuses: "All statuses",
    allDelivery: "All delivery",
    noSummary: "No summary.",
    runAt: "Run at",
    staggerWindow: "Stagger window",
    payloadText: "Payload text",
    wakeMode: "Wake mode",
    resultDelivery: "Result delivery",
    statusOk: "OK",
    statusError: "Error",
    statusSkipped: "Skipped",
    deliveryDelivered: "Delivered",
    deliveryNotDelivered: "Not delivered",
    deliveryUnknown: "Unknown",
    deliveryNotRequested: "Not requested",
  },
  skills: {
    cardTitle: "Skills",
    cardSub: "Bundled, managed, and workspace skills.",
    filter: "Filter",
    searchPlaceholder: "Search skills",
    shown: "{count} shown",
    noSkills: "No skills found.",
    enable: "Enable",
    disable: "Disable",
    installing: "Installing…",
    missing: "Missing",
    reason: "Reason",
    groupWorkspace: "Workspace",
    groupBuiltIn: "Built-in",
  },
  nodes: {
    cardTitle: "Nodes",
    cardSub: "Paired devices and live links.",
    noNodes: "No nodes found.",
    devicesTitle: "Devices",
    devicesSub: "Pairing requests + role tokens.",
    pending: "Pending",
    paired: "Paired",
    noPairedDevices: "No paired devices.",
    approve: "Approve",
    reject: "Reject",
    requested: "requested",
    roleLabel: "role",
    tokens: "Tokens",
    tokensNone: "none",
    rotate: "Rotate",
    revoke: "Revoke",
    bindingTitle: "Exec node binding",
    bindingSub: "Pin agents to a specific node when using",
    bindingSubMono: "exec host=node",
    loadConfig: "Load config",
    loadConfigHint: "Load config to edit bindings.",
    switchToForm: "Switch the Config tab to Form mode to edit bindings here.",
    defaultBinding: "Default binding",
    defaultBindingSub: "Used when agents do not override a node binding.",
    node: "Node",
    anyNode: "Any node",
    noNodesWithRun: "No nodes with system.run available.",
    noAgentsFound: "No agents found.",
    agentBinding: "Agent",
    useDefault: "Use default",
    bindingLabel: "Binding",
    defaultAgent: "default agent",
    agent: "agent",
    usesDefault: "uses default ({binding})",
    override: "override",
  },
  debug: {
    snapshotsTitle: "Snapshots",
    snapshotsSub: "Status, health, and heartbeat data.",
    status: "Status",
    health: "Health",
    lastHeartbeat: "Last heartbeat",
    securityAudit: "Security audit",
    criticalCount: "{count} critical",
    warningsCount: "{count} warnings",
    noCritical: "No critical issues",
    infoCount: "{count} info",
    securityAuditHint: "Run",
    securityAuditCommand: "openclaw security audit --deep",
    securityAuditForDetails: "for details.",
    manualRpcTitle: "Manual RPC",
    manualRpcSub: "Send a raw gateway method with JSON params.",
    method: "Method",
    paramsJson: "Params (JSON)",
    call: "Call",
    modelsTitle: "Models",
    modelsSub: "Catalog from models.list.",
    eventLogTitle: "Event Log",
    eventLogSub: "Latest gateway events.",
    noEvents: "No events yet.",
  },
  logs: {
    cardTitle: "Logs",
    cardSub: "Gateway file logs (JSONL).",
    exportLabel: "Export {label}",
    filter: "Filter",
    searchPlaceholder: "Search logs",
    autoFollow: "Auto-follow",
    file: "File",
    truncated: "Log output truncated; showing latest chunk.",
    noEntries: "No log entries.",
    filtered: "filtered",
    visible: "visible",
  },
  execApprovals: {
    title: "Exec approvals",
    subtitle: "Allowlist and approval policy for ",
    subtitleMono: "exec host=gateway/node",
    subtitleAfter: ".",
    save: "Save",
    saving: "Saving…",
    loadHint: "Load exec approvals to edit allowlists.",
    loadApprovals: "Load approvals",
    target: "Target",
    targetSub: "Gateway edits local approvals; node edits the selected node.",
    host: "Host",
    gateway: "Gateway",
    node: "Node",
    selectNode: "Select node",
    noNodesAdvertise: "No nodes advertise exec approvals yet.",
    scope: "Scope",
    defaults: "Defaults",
    security: "Security",
    securitySubDefault: "Default security mode.",
    securitySubOverride: "Default: {value}.",
    mode: "Mode",
    useDefaultValue: "Use default ({value})",
    deny: "Deny",
    allowlist: "Allowlist",
    full: "Full",
    ask: "Ask",
    askSubDefault: "Default prompt policy.",
    askSubOverride: "Default: {value}.",
    off: "Off",
    onMiss: "On miss",
    always: "Always",
    askFallback: "Ask fallback",
    askFallbackSubDefault: "Applied when the UI prompt is unavailable.",
    askFallbackSubOverride: "Default: {value}.",
    fallback: "Fallback",
    autoAllowSkills: "Auto-allow skill CLIs",
    autoAllowSkillsSubDefault: "Allow skill executables listed by the Gateway.",
    autoAllowSkillsSubUsing: "Using default ({value}).",
    autoAllowSkillsSubOverride: "Override ({value}).",
    enabled: "Enabled",
    useDefault: "Use default",
    on: "on",
    offLabel: "off",
    allowlistTitle: "Allowlist",
    allowlistSub: "Case-insensitive glob patterns.",
    addPattern: "Add pattern",
    noAllowlistEntries: "No allowlist entries yet.",
    newPattern: "New pattern",
    lastUsed: "Last used",
    pattern: "Pattern",
    remove: "Remove",
    never: "never",
  },
};
